import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import SaleListService from "../../services/SaleListService";
import FinancialAccountService from "../../services/FinancialAccountService";
import SupplierService from "../../services/SupplierService";
import api from "../../api/axios";
// src/pages/Sales/components/SalesPaymentsDialog.jsx

const currency = new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY" });
const fmtMoney = (v) => currency.format(v ?? 0);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("tr-TR") : "");

const PAYMENT_METHODS = [
  { label: "Nakit", value: "Cash" },
  { label: "Kredi Kartı", value: "Card" },
  { label: "Sanal POS", value: "SanalPos" },
  { label: "Çek", value: "Çek" },
  { label: "Senet", value: "Senet" },
  { label: "Havale/EFT", value: "Transfer" },
];

export default function SalesPaymentsDialog({
  saleId,
  visible,
  onHide,
  onChanged, // ödeme eklendiğinde üst listeyi yenilemek için callback
}) {
  const toast = useRef(null);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  // Yeni ödeme formu
  const [addOpen, setAddOpen] = useState(false);
  const [method, setMethod] = useState(null);
  const [amount, setAmount] = useState(null);
  const [paymentDate, setPaymentDate] = useState(new Date());
  const [referenceNo, setReferenceNo] = useState("");
  const [note, setNote] = useState("");
  const [financialAccounts, setFinancialAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [selectedSupplierId, setSelectedSupplierId] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [mounted, setMounted] = useState(false);

  const [chequeDetails, setChequeDetails] = useState({
    bankName: "",
    bankBranch: "",
    checkNumber: "",
    dueDate: null,
    drawer: ""
  });

  const [promissoryDetails, setPromissoryDetails] = useState({
    dueDate: null,
    drawer: "",
    guarantor: ""
  });

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    if (visible) {
      loadPayments();
      FinancialAccountService.getAll().then(res => {
        if (res.success) {
          setFinancialAccounts(res.data || []);
        }
      });
      SupplierService.getAllSuppliers().then(data => {
        setSuppliers(data || []);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, saleId]);

  const filteredAccounts = React.useMemo(() => {
    if (method === "Cash") return financialAccounts.filter(a => a.accountType === 1);
    if (method === "Transfer") return financialAccounts.filter(a => a.accountType === 2);
    if (method === "Card") return financialAccounts.filter(a => a.accountType === 4);
    if (method === "SanalPos") return financialAccounts.filter(a => a.accountType === 5);
    return [];
  }, [financialAccounts, method]);

  const handleSupplierChange = (supplierId) => {
    setSelectedSupplierId(supplierId);
    const matchedAcc = financialAccounts.find(
      a => a.accountType === 5 && a.supplierId === supplierId
    );
    if (matchedAcc) {
      setSelectedAccountId(matchedAcc.id);
    } else {
      setSelectedAccountId(null);
    }
  };

  const handleAccountChange = (accountId) => {
    setSelectedAccountId(accountId);
    const selectedAcc = financialAccounts.find(a => a.id === accountId);
    if (selectedAcc && selectedAcc.supplierId) {
      setSelectedSupplierId(selectedAcc.supplierId);
    } else {
      setSelectedSupplierId(null);
    }
  };

  useEffect(() => {
    if (method === "SanalPos") return;
    if (filteredAccounts.length === 1) {
      setSelectedAccountId(filteredAccounts[0].id);
    } else {
      setSelectedAccountId(null);
    }
  }, [filteredAccounts, method]);

  const loadPayments = async () => {

    try {
      setLoading(true);
      const res = await SaleListService.getPayments(saleId);
      setRows(Array.isArray(res) ? res : []);
    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: err?.message || "Ödemeler alınamadı.",
      });
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setMethod(null);
    setAmount(null);
    setPaymentDate(new Date());
    setReferenceNo("");
    setNote("");
    setSelectedAccountId(null);
    setSelectedSupplierId(null);
  };

  const savePayment = async () => {
    // basit validasyon
    if (!method) {
      toast.current?.show({ severity: "warn", summary: "Uyarı", detail: "Ödeme yöntemi seçin." });
      return;
    }
    if (!amount || amount <= 0) {
      toast.current?.show({ severity: "warn", summary: "Uyarı", detail: "Tutar girin." });
      return;
    }
    if (method === "SanalPos") {
      if (!selectedAccountId && !selectedSupplierId) {
        toast.current?.show({ severity: "warn", summary: "Uyarı", detail: "Sanal POS işleminde hesap veya tedarikçi seçilmelidir." });
        return;
      }
    }
    try {
      setLoading(true);
      const payload = {
        method,
        amount,
        paymentDate: paymentDate?.toISOString(), // backend DateTime alıyor
        referenceNo,
        note,
        financialAccountId: selectedAccountId,
        supplierId: method === "SanalPos" ? selectedSupplierId : null,
        ...(method === "Çek" ? chequeDetails : {}),
        ...(method === "Senet" ? promissoryDetails : {}),
      };
      await SaleListService.addPayment(saleId, payload);
      // Başarılı
      toast.current?.show({ severity: "success", summary: "Kaydedildi", detail: "Ödeme eklendi." });
      setAddOpen(false);
      resetForm();
      await loadPayments();   // listeyi yenile
      onChanged?.();          // üst grid’i yenilemek istersen
    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: err?.response?.data?.message || err?.message || "Ödeme eklenemedi.",
      });
    } finally {
      setLoading(false);
    }
  };

  const paymentsHeader = (
    <div className="flex items-center gap-3">
      <div className="!bg-blue-600 w-3 h-12 rounded-xl flex items-center justify-center text-white shadow-md !shadow-blue-500/20 flex-shrink-0">
        <i className="pi pi-credit-card text-lg" />
      </div>
      <div>
        <h3 className="text-sm font-black text-slate-800 m-0 uppercase tracking-tight">Kayıtlı Ödemeler</h3>
        <p className="text-[9px] text-slate-400 font-bold m-0 uppercase tracking-wider">Satış ödeme geçmişi ve tahsilatlar</p>
      </div>
    </div>
  );

  const addHeader = (
    <div className="flex items-center gap-3">
      <div className="bg-emerald-600 w-3 h-12 rounded-xl flex items-center justify-center text-white shadow-md shadow-emerald-500/20 flex-shrink-0">
        <i className="pi pi-plus text-lg" />
      </div>
      <div>
        <h3 className="text-sm font-black text-slate-800 m-0 uppercase tracking-tight">Yeni Ödeme Girişi</h3>
        <p className="text-[9px] text-slate-400 font-bold m-0 uppercase tracking-wider">Tahsilat veya ödeme kaydı ekleyin</p>
      </div>
    </div>
  );

  const footer = (
    <div className="flex justify-end gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
      <Button
        label="Kapat"
        icon="pi pi-times"
        className="p-button-text p-button-secondary rounded-xl font-bold px-4 py-2"
        onClick={onHide}
      />
      <Button
        label="Yeni Ödeme Ekle"
        icon="pi pi-plus"
        className="!bg-emerald-600 !border-none text-white rounded-xl font-black px-5 py-2.5 shadow-lg shadow-emerald-500/20 transition-all active:scale-95"
        onClick={() => setAddOpen(true)}
      />
    </div>
  );

  const addFooter = (
    <div className="flex justify-end gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
      <Button
        label="Vazgeç"
        icon="pi pi-times"
        className="p-button-text p-button-secondary rounded-xl font-bold px-4 py-2"
        onClick={() => { setAddOpen(false); resetForm(); }}
      />
      <Button
        label="Kaydet"
        icon="pi pi-check"
        className="!bg-emerald-600 !border-none text-white rounded-xl font-black px-5 py-2.5 shadow-lg shadow-emerald-500/20 transition-all active:scale-95"
        onClick={savePayment}
      />
    </div>
  );

  const methodBody = (row) => {
    const m = PAYMENT_METHODS.find((x) => x.value === (row.method ?? row.Method));
    return (
      <Tag
        value={m?.label || row.method || row.Method}
        className="!bg-slate-100 !text-slate-600 border border-slate-200/50 font-black text-[10px] px-2.5 py-1 rounded-md"
      />
    );
  };

  return (
    <>
      <Toast ref={toast} />

      {/* Ödeme Detay Dialog */}
      <Dialog
        header={paymentsHeader}
        visible={visible}
        onHide={onHide}
        style={{ width: "750px", maxWidth: "95vw" }}
        footer={footer}
        modal
        className="custom-dialog"
      >
        <DataTable
          value={rows}
          loading={loading}
          stripedRows
          className="mt-2 text-sm"
          emptyMessage={
            <div className="flex flex-col items-center gap-2 py-8 text-slate-400">
              <i className="pi pi-info-circle text-3xl"></i>
              <span className="font-bold">Bu satışa ait henüz herhangi bir ödeme kaydı bulunmuyor.</span>
            </div>
          }
        >
          <Column
            field="paymentDate"
            header="Tarih"
            body={(r) => fmtDate(r?.paymentDate || r.PaymentDate)}
            className="font-bold text-slate-600"
            headerClassName="bg-slate-50/80 text-[10px] font-black uppercase py-3"
          />
          <Column
            field="method"
            header="Yöntem"
            body={methodBody}
            headerClassName="bg-slate-50/80 text-[10px] font-black uppercase py-3"
          />
          <Column
            field="amount"
            header="Tutar"
            body={(r) => fmtMoney(r?.amount ?? r.Amount)}
            className="font-black text-slate-800"
            headerClassName="bg-slate-50/80 text-[10px] font-black uppercase py-3"
          />
          <Column
            field="referenceNo"
            header="Ref. No"
            className="font-medium text-slate-500"
            headerClassName="bg-slate-50/80 text-[10px] font-black uppercase py-3"
          />
          <Column
            field="note"
            header="Açıklama"
            className="font-medium text-slate-500"
            headerClassName="bg-slate-50/80 text-[10px] font-black uppercase py-3"
          />
        </DataTable>
      </Dialog>

      {/* Yeni Ödeme Ekle Dialog */}
      <Dialog
        header={addHeader}
        visible={addOpen}
        onHide={() => { setAddOpen(false); resetForm(); }}
        style={{ width: "1072px", maxWidth: "95vw", minHeight: "550px" }}
        footer={addFooter}
        modal
        className="custom-dialog"
      >
        <div className="p-fluid grid grid-cols-1 md:grid-cols-2 gap-6 py-4 mt-2">
          {/* Ödeme Yöntemi */}
          <div className="col-span-1 space-y-1.5 relative z-30">
            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1 block">Ödeme Yöntemi</label>
            <Dropdown
              value={method}
              options={PAYMENT_METHODS}
              onChange={(e) => {
                setMethod(e.value);
                setSelectedAccountId(null);
                setSelectedSupplierId(null);
              }}
              placeholder="Seçin"
              className="w-full rounded-xl border-slate-200 bg-slate-50/50 font-bold text-sm"
              appendTo="self"
            />
          </div>

          {/* İşlem Yapılacak Hesap */}
          {(filteredAccounts.length > 0 || method === "SanalPos") && (
            <div className="col-span-1 space-y-1.5 animate-fade-in relative z-30">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1 block">İşlem Yapılacak Hesap</label>
              <Dropdown
                value={selectedAccountId}
                options={filteredAccounts}
                optionLabel="name"
                optionValue="id"
                onChange={(e) => handleAccountChange(e.value)}
                placeholder="Hesap seçin..."
                className="w-full rounded-xl border-slate-200 bg-slate-50/50 font-bold text-sm"
                appendTo="self"
              />
            </div>
          )}

          {method === "SanalPos" && (
            <div className="col-span-1 space-y-1.5 animate-fade-in relative z-20">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1 block">Tedarikçi</label>
              <Dropdown
                value={selectedSupplierId}
                options={suppliers}
                optionLabel="name"
                optionValue="id"
                filter
                onChange={(e) => handleSupplierChange(e.value)}
                placeholder="Tedarikçi seçin..."
                className="w-full rounded-xl border-slate-200 bg-slate-50/50 font-bold text-sm"
                appendTo="self"
              />
            </div>
          )}

          {/* Tutar */}
          <div className="col-span-1 space-y-1.5 relative z-10">
            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1 block">Tutar</label>
            <InputNumber
              value={amount}
              onValueChange={(e) => setAmount(e.value)}
              mode="currency"
              currency="TRY"
              locale="tr-TR"
              className="w-full rounded-xl"
              inputClassName="rounded-xl border-slate-200 bg-slate-50/50 font-bold text-sm py-2.5 px-3.5 focus:bg-white transition-all shadow-sm text-slate-800"
            />
          </div>

          {/* Tarih */}
          <div className="col-span-1 space-y-1.5 relative z-10">
            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1 block">Tarih</label>
            <Calendar
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.value ?? new Date())}
              showIcon
              readOnlyInput
              dateFormat="dd.mm.yy"
              appendTo="self"
              className="w-full rounded-xl"
              inputClassName="rounded-xl border-slate-200 bg-slate-50/50 font-bold text-sm py-2 px-3 focus:bg-white transition-all shadow-sm"
            />
          </div>

          {/* Referans No */}
          <div className="col-span-1 space-y-1.5">
            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1 block">Referans No</label>
            <InputText
              value={referenceNo}
              onChange={(e) => setReferenceNo(e.target.value)}
              placeholder="Opsiyonel"
              className="w-full rounded-xl border-slate-200 bg-slate-50/50 font-bold text-sm py-2.5 px-3.5 focus:bg-white transition-all shadow-sm"
            />
          </div>

          {/* Açıklama */}
          <div className="col-span-1 space-y-1.5">
            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1 block">Açıklama</label>
            <InputText
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Ödeme notu..."
              className="w-full rounded-xl border-slate-200 bg-slate-50/50 font-bold text-sm py-2.5 px-3.5 focus:bg-white transition-all shadow-sm"
            />
          </div>

          {/* Çek Detay Kartı */}
          {method === "Çek" && (
            <div className="col-span-2 bg-slate-50/50 border border-slate-100 p-4 rounded-2xl grid grid-cols-2 gap-4 mt-2 animate-in fade-in duration-300">
              <div className="col-span-2 flex items-center gap-2 pb-1.5 border-b border-slate-200/50">
                <i className="pi pi-file text-emerald-600 text-xs"></i>
                <span className="text-[10px] font-black text-slate-600 uppercase tracking-wider">Çek Detay Bilgileri</span>
              </div>
              <div className="col-span-1 space-y-1.5">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-wider ml-1 block">Banka</label>
                <InputText value={chequeDetails.bankName} onChange={e => setChequeDetails({ ...chequeDetails, bankName: e.target.value })} className="rounded-xl border-slate-200 bg-white font-bold text-sm py-2 px-3 focus:bg-white transition-all shadow-sm" />
              </div>
              <div className="col-span-1 space-y-1.5">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-wider ml-1 block">Şube</label>
                <InputText value={chequeDetails.bankBranch} onChange={e => setChequeDetails({ ...chequeDetails, bankBranch: e.target.value })} className="rounded-xl border-slate-200 bg-white font-bold text-sm py-2 px-3 focus:bg-white transition-all shadow-sm" />
              </div>
              <div className="col-span-1 space-y-1.5">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-wider ml-1 block">Çek No</label>
                <InputText value={chequeDetails.checkNumber} onChange={e => setChequeDetails({ ...chequeDetails, checkNumber: e.target.value })} className="rounded-xl border-slate-200 bg-white font-bold text-sm py-2 px-3 focus:bg-white transition-all shadow-sm" />
              </div>
              <div className="col-span-1 space-y-1.5">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-wider ml-1 block">Vade Tarihi</label>
                <Calendar value={chequeDetails.dueDate} onChange={e => setChequeDetails({ ...chequeDetails, dueDate: e.value })} dateFormat="dd.mm.yy" showIcon appendTo="self" className="w-full rounded-xl" inputClassName="rounded-xl border-slate-200 bg-white font-bold text-sm py-2 px-3 focus:bg-white transition-all shadow-sm" />
              </div>
              <div className="col-span-2 space-y-1.5">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-wider ml-1 block">Keşideci</label>
                <InputText value={chequeDetails.drawer} onChange={e => setChequeDetails({ ...chequeDetails, drawer: e.target.value })} className="rounded-xl border-slate-200 bg-white font-bold text-sm py-2 px-3 focus:bg-white transition-all shadow-sm" />
              </div>
            </div>
          )}

          {/* Senet Detay Kartı */}
          {method === "Senet" && (
            <div className="col-span-2 bg-slate-50/50 border border-slate-100 p-4 rounded-2xl grid grid-cols-2 gap-4 mt-2 animate-in fade-in duration-300">
              <div className="col-span-2 flex items-center gap-2 pb-1.5 border-b border-slate-200/50">
                <i className="pi pi-file text-emerald-600 text-xs"></i>
                <span className="text-[10px] font-black text-slate-600 uppercase tracking-wider">Senet Detay Bilgileri</span>
              </div>
              <div className="col-span-1 space-y-1.5">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-wider ml-1 block">Vade Tarihi</label>
                <Calendar value={promissoryDetails.dueDate} onChange={e => setPromissoryDetails({ ...promissoryDetails, dueDate: e.value })} dateFormat="dd.mm.yy" showIcon appendTo="self" className="w-full rounded-xl" inputClassName="rounded-xl border-slate-200 bg-white font-bold text-sm py-2 px-3 focus:bg-white transition-all shadow-sm" />
              </div>
              <div className="col-span-1 space-y-1.5">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-wider ml-1 block">Borçlu</label>
                <InputText value={promissoryDetails.drawer} onChange={e => setPromissoryDetails({ ...promissoryDetails, drawer: e.target.value })} className="rounded-xl border-slate-200 bg-white font-bold text-sm py-2 px-3 focus:bg-white transition-all shadow-sm" />
              </div>
              <div className="col-span-2 space-y-1.5">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-wider ml-1 block">Kefil</label>
                <InputText value={promissoryDetails.guarantor} onChange={e => setPromissoryDetails({ ...promissoryDetails, guarantor: e.target.value })} className="rounded-xl border-slate-200 bg-white font-bold text-sm py-2 px-3 focus:bg-white transition-all shadow-sm" />
              </div>
            </div>
          )}
        </div>
      </Dialog>
    </>
  );
}
